import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { api, apiError } from "../../lib/api";
import { Atmosphere, Shell } from "../../components/site/Primitives";
import Wordmark from "../../components/site/Wordmark";

const AuthShell = ({ children, title, sub, testId }) => (
  <div data-testid={testId} className="relative min-h-screen overflow-hidden bg-elv-base">
    <Atmosphere />
    <Shell className="relative flex min-h-screen items-center py-16">
      <div className="grid w-full gap-14 lg:grid-cols-[1fr_0.9fr]">
        <div className="hidden flex-col justify-center lg:flex">
          <Wordmark />
          <h1 className="mt-12 max-w-md text-4xl font-semibold leading-[1.06] text-elv-text">
            One operating system for the whole customer journey.
          </h1>
          <p className="mt-6 max-w-md text-sm text-elv-dim">
            Leads, conversations, appointments, follow-ups, payments and reporting — in one interface, scoped to
            your business.
          </p>
          <ul className="mt-10 space-y-3">
            {["Organisation-isolated data", "Role-based access", "Demo environment available"].map((i) => (
              <li key={i} className="flex items-center gap-2.5 text-sm text-elv-dim">
                <Icons.ShieldCheck className="h-4 w-4 text-elv-green" />
                {i}
              </li>
            ))}
          </ul>
        </div>
        <div className="glass rounded-xl p-6 sm:p-8">
          <div className="lg:hidden">
            <Wordmark compact />
          </div>
          <h2 className="mt-6 text-2xl font-semibold text-elv-text lg:mt-0">{title}</h2>
          <p className="mt-2 text-sm text-elv-dim">{sub}</p>
          {children}
        </div>
      </div>
    </Shell>
  </div>
);

const Input = ({ id, label, type = "text", value, onChange, error, autoComplete }) => (
  <div>
    <label htmlFor={id} className="text-[11px] uppercase tracking-[0.2em] text-elv-dim">
      {label}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
      data-testid={`auth-input-${id}`}
      className="mt-2.5 w-full rounded-md border border-elv-line bg-white/[0.02] px-3.5 py-3 text-sm text-elv-text outline-none focus:border-elv-green/50"
    />
    {error ? (
      <p data-testid={`auth-error-${id}`} className="mt-2 text-xs text-red-300">
        {error}
      </p>
    ) : null}
  </div>
);

const SubmitButton = ({ loading, children, testId }) => (
  <button
    type="submit"
    data-testid={testId}
    disabled={loading}
    className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-md bg-elv-green px-6 py-3 text-sm font-semibold text-[#04120A] transition-colors duration-300 hover:bg-[#5cffa0] disabled:opacity-60"
  >
    {loading ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : null}
    {children}
  </button>
);

const GoogleButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid="auth-google-button"
    className="glass inline-flex w-full items-center justify-center gap-2 rounded-md px-6 py-3 text-sm font-medium text-elv-text transition-colors duration-300 hover:border-elv-cyan/40"
  >
    <Icons.Chrome className="h-4 w-4 text-elv-cyan" />
    Continue with Google
  </button>
);

export function Login() {
  const { login, startGoogleLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\S+@\S+\.\S+$/.test(email)) return setError("Enter a valid email address.");
    if (!password) return setError("Enter your password.");
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success(`Welcome back, ${data.user.name}.`);
      navigate(data.user.is_superadmin ? "/admin" : "/app/dashboard");
    } catch (err) {
      setError(apiError(err, "Unable to sign in."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell testId="login-page" title="Client login" sub="Access your Elvoria AI operating system.">
      <form onSubmit={submit} noValidate className="mt-7 grid gap-5">
        <Input id="email" label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
        <Input id="password" label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
        {error ? (
          <p data-testid="login-error" className="rounded-md border border-red-400/25 bg-red-400/[0.06] px-3 py-2.5 text-xs text-red-300">
            {error}
          </p>
        ) : null}
        <SubmitButton loading={loading} testId="login-submit">
          {loading ? "Signing in…" : "Sign in"}
        </SubmitButton>
      </form>
      <div className="my-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-elv-dim">
        <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
      </div>
      <GoogleButton onClick={startGoogleLogin} />
      <div className="mt-7 flex flex-wrap items-center justify-between gap-3 text-xs text-elv-dim">
        <Link to="/forgot-password" data-testid="login-forgot-link" className="hover:text-elv-text">
          Forgot password?
        </Link>
        <Link to="/signup" data-testid="login-signup-link" className="text-elv-green hover:text-elv-cyan">
          Create an account
        </Link>
      </div>
      <div className="mt-6 rounded-md border border-elv-cyan/20 bg-elv-cyan/[0.04] p-3.5 text-[11px] leading-relaxed text-elv-dim">
        <span className="text-elv-cyan">Demo environment:</span> sign in with{" "}
        <span className="font-mono text-elv-text">demo@elvoria.ai</span> /{" "}
        <span className="font-mono text-elv-text">ElvoriaDemo!2026</span> to explore the client portal with
        fictional data.
      </div>
    </AuthShell>
  );
}

export function Signup() {
  const { register, startGoogleLogin } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", business_name: "", email: "", password: "", industry: "Dental" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const n = {};
    if (form.name.trim().length < 2) n.name = "Enter your name.";
    if (form.business_name.trim().length < 2) n.business_name = "Enter your business name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) n.email = "Enter a valid email address.";
    if (form.password.length < 8) n.password = "Use at least 8 characters.";
    setErrors(n);
    if (Object.keys(n).length) return;
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created. Your workspace is ready.");
      navigate("/app/onboarding");
    } catch (err) {
      setErrors({ form: apiError(err, "Unable to create the account.") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      testId="signup-page"
      title="Create your account"
      sub="Your workspace is created with a demo dataset so you can see the system working immediately."
    >
      <form onSubmit={submit} noValidate className="mt-7 grid gap-5">
        <Input id="name" label="Your name" value={form.name} onChange={set("name")} error={errors.name} />
        <Input id="business_name" label="Business name" value={form.business_name} onChange={set("business_name")} error={errors.business_name} />
        <Input id="email" label="Email" type="email" value={form.email} onChange={set("email")} error={errors.email} autoComplete="email" />
        <Input id="password" label="Password" type="password" value={form.password} onChange={set("password")} error={errors.password} autoComplete="new-password" />
        <div>
          <label htmlFor="industry" className="text-[11px] uppercase tracking-[0.2em] text-elv-dim">
            Industry
          </label>
          <select
            id="industry"
            value={form.industry}
            onChange={set("industry")}
            data-testid="auth-input-industry"
            className="mt-2.5 w-full rounded-md border border-elv-line bg-[#0A0F14] px-3.5 py-3 text-sm text-elv-text outline-none focus:border-elv-green/50"
          >
            {["Dental", "HVAC", "Plumbing", "Roofing", "Electrician", "Cleaning", "Med Spa", "Law Firm", "Real Estate", "Auto Repair", "Salon", "Fitness", "Other"].map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
        </div>
        {errors.form ? (
          <p data-testid="signup-error" className="rounded-md border border-red-400/25 bg-red-400/[0.06] px-3 py-2.5 text-xs text-red-300">
            {errors.form}
          </p>
        ) : null}
        <SubmitButton loading={loading} testId="signup-submit">
          {loading ? "Creating account…" : "Create account"}
        </SubmitButton>
      </form>
      <div className="my-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-elv-dim">
        <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
      </div>
      <GoogleButton onClick={startGoogleLogin} />
      <p className="mt-7 text-xs text-elv-dim">
        Already have an account?{" "}
        <Link to="/login" data-testid="signup-login-link" className="text-elv-green hover:text-elv-cyan">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setMessage("Enter a valid email address.");
      setState("error");
      return;
    }
    setState("loading");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMessage(data.message);
      setState("success");
    } catch (err) {
      setMessage(apiError(err));
      setState("error");
    }
  };

  return (
    <AuthShell
      testId="forgot-password-page"
      title="Reset your password"
      sub="Enter the email on your account and we'll send a reset link."
    >
      <form onSubmit={submit} noValidate className="mt-7 grid gap-5">
        <Input id="email" label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        {message ? (
          <p
            data-testid="forgot-message"
            className={`rounded-md border px-3 py-2.5 text-xs ${
              state === "success"
                ? "border-elv-green/25 bg-elv-green/[0.06] text-elv-green"
                : "border-red-400/25 bg-red-400/[0.06] text-red-300"
            }`}
          >
            {message}
          </p>
        ) : null}
        <SubmitButton loading={state === "loading"} testId="forgot-submit">
          {state === "loading" ? "Sending…" : "Send reset link"}
        </SubmitButton>
      </form>
      <p className="mt-7 text-xs text-elv-dim">
        <Link to="/login" data-testid="forgot-back-link" className="text-elv-green hover:text-elv-cyan">
          Back to sign in
        </Link>
      </p>
    </AuthShell>
  );
}
